import { Check, CheckCheck, FileText, MapPin, ImageOff, Download, Bot } from "lucide-react";
import { useChatFile, fmtClock, fmtSize, hoursLeft } from "@/lib/chat";
import { mapsUrl } from "@/lib/photo";

const Ticks = ({ read }) => read
  ? <CheckCheck className="h-3.5 w-3.5 text-sky-500" data-testid="chat-tick-read" aria-label="Sudah dibaca" />
  : <Check className="h-3.5 w-3.5 text-slate-400" data-testid="chat-tick-sent" aria-label="Terkirim" />;

const Expired = () => (
  <div className="flex items-center gap-2 text-xs italic text-slate-500 py-1"><ImageOff className="h-4 w-4" /> File sudah dihapus otomatis (12 jam)</div>
);

const FileBody = ({ m }) => {
  const { url, gone } = useChatFile(m);
  if (gone) return <Expired />;
  if (m.type === "image") {
    return url
      ? <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="Gambar" className="rounded-lg max-h-64 w-auto max-w-full object-cover" data-testid={`chat-image-${m.msg_id}`} /></a>
      : <div className="h-40 w-52 rounded-lg bg-black/5 animate-pulse" />;
  }
  if (m.type === "audio") {
    return url ? <audio controls src={url} className="w-60 max-w-full h-10" data-testid={`chat-audio-${m.msg_id}`} /> : <div className="h-10 w-56 rounded-full bg-black/5 animate-pulse" />;
  }
  return (
    <a href={url || undefined} download={m.file_name} target="_blank" rel="noreferrer" data-testid={`chat-pdf-${m.msg_id}`}
      className="flex items-center gap-3 bg-black/5 rounded-lg p-2.5 min-w-[200px] max-w-[260px]">
      <span className="h-10 w-10 rounded-lg bg-red-600 text-white flex items-center justify-center shrink-0"><FileText className="h-5 w-5" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold truncate">{m.file_name}</span>
        <span className="block text-[11px] text-slate-500">PDF • {fmtSize(m.size)}</span>
      </span>
      <Download className="h-4 w-4 text-slate-500" />
    </a>
  );
};

const Location = ({ m }) => (
  <a href={mapsUrl(m)} target="_blank" rel="noreferrer" data-testid={`chat-location-${m.msg_id}`}
    className="flex items-center gap-3 bg-black/5 rounded-lg p-2.5 min-w-[200px]">
    <span className="h-10 w-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0"><MapPin className="h-5 w-5" /></span>
    <span>
      <span className="block text-sm font-semibold">Tikor Lokasi</span>
      <span className="block text-[11px] text-slate-600 font-mono">{m.lat.toFixed(6)}, {m.lng.toFixed(6)}</span>
      <span className="block text-[11px] text-blue-700 font-semibold">Buka di Google Maps</span>
    </span>
  </a>
);

export const ChatBubble = ({ m, viewer }) => {
  if (m.type === "system") {
    return (
      <div className="flex justify-center my-2" data-testid={`chat-system-${m.msg_id}`}>
        <span className="text-[11px] bg-amber-50 text-amber-800 border border-amber-200 rounded-lg px-3 py-1 shadow-sm">{m.text}</span>
      </div>
    );
  }
  const right = m.sender === "admin";
  const own = m.sender === viewer;
  const showName = viewer === "admin" || m.sender === "admin";
  const isFile = ["image", "audio", "pdf"].includes(m.type);
  return (
    <div className={`flex ${right ? "justify-end" : "justify-start"} my-1`} data-testid={`chat-msg-${m.msg_id}`}>
      <div className={`relative max-w-[80%] rounded-xl px-2.5 pt-1.5 pb-1 shadow-sm text-slate-900 ${right ? "bg-[#d9fdd3] rounded-tr-sm" : "bg-white rounded-tl-sm"}`}>
        {showName && <div className={`text-[11px] font-bold mb-0.5 flex items-center gap-1 ${m.auto ? "text-amber-700" : right ? "text-emerald-700" : "text-blue-700"}`} data-testid={m.auto ? `chat-auto-${m.msg_id}` : undefined}>{m.auto && <Bot className="h-3.5 w-3.5" />}{m.auto ? "Balasan otomatis" : m.sender_name}</div>}
        {m.type === "text" && <p className="text-sm whitespace-pre-wrap break-words pr-12">{m.text}</p>}
        {isFile && <FileBody m={m} />}
        {m.type === "location" && <Location m={m} />}
        <div className="flex items-center justify-end gap-1 text-[10px] text-slate-500 -mt-0.5">
          {isFile && !m.expired && m.expires_at && <span className="mr-1">hapus otomatis {hoursLeft(m.expires_at)}j</span>}
          {fmtClock(m.created_at)}
          {own && <Ticks read={!!m.read_at} />}
        </div>
      </div>
    </div>
  );
};
